<template>
  <div class="flex flex-col">
    <div class="font-bold text-text-neutral-extreme text-[16px] leading-[24px] mb-7" v-if="showHeader">{{ $t('session_settings') }}</div>
    <div v-if="isRoomHost" class="mb-4 max-[800px]:mb-[18px]">
      <label class="block text-text-neutral-high text-[14px] leading-[20px] font-medium mb-2" :for="`#settings_title_${salt}`">{{ $t('session_setup_title') }}</label>
      <input type="text" :name="`title_${salt}`" v-model="sessionTitle" class="border-2 border-[#292F36] rounded-[10px] bg-transparent text-text-neutral-extreme font-normal text-[16px] leading-[1.18rem] h-[44px] w-full px-[10px] outline-none focus:border-[#6CBFFF]" placeholder="" :id="`settings_title_${salt}`">
    </div>
    <div class="flex flex-col grow w-full">
      <div class="flex flex-col">    
        <SwitchGroup v-if="isRoomHost" as="div" class="flex justify-between mb-4">
          <span class="flex grow flex-col">
            <SwitchLabel as="span" class="font-medium text-text-neutral-extreme text-[16px] leading-[24px]" passive>{{ $t('settings_autostop_title') }}</SwitchLabel>
            <SwitchDescription as="span" class="text-[14px] leading-[20px] text-text-neutral-medium">{{ $t('session_setup_autostop_description') !== 'session_setup_autostop_description' ? $t('session_setup_autostop_description') : 'Microphone will stop after 30 minutes of silence to preserve caption/translation time' }}</SwitchDescription>
          </span>
          <Switch v-model="settings.is_autostop_enabled" :class="[settings.is_autostop_enabled ? 'bg-primary' : 'bg-gray-700', 'relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden']">
            <span aria-hidden="true" :class="[settings.is_autostop_enabled ? 'translate-x-5 bg-gray-800' : 'translate-x-0 bg-white', 'pointer-events-none inline-block size-5 transform rounded-full shadow-sm ring-0 transition duration-200 ease-in-out mt-0.5']" />
          </Switch>
        </SwitchGroup>
        <SwitchGroup v-if="isRoomHost" as="div" class="flex justify-between mb-6">
          <span class="flex grow flex-col">
            <SwitchLabel as="span" class="font-medium text-text-neutral-extreme text-[16px] leading-[24px]" passive>{{ $t('custom_terms_profanity_filter') }}</SwitchLabel>
            <SwitchDescription as="span" class="text-[12px] text-text-neutral-medium opacity-75 max-w-[95%] md:max-w-72"></SwitchDescription>
          </span>
          <Switch v-model="settings.profanity_filter" :class="[settings.profanity_filter ? 'bg-primary' : 'bg-gray-700', 'relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden']">
            <span aria-hidden="true" :class="[settings.profanity_filter ? 'translate-x-5 bg-gray-800' : 'translate-x-0 bg-white', 'pointer-events-none inline-block size-5 transform rounded-full shadow-sm ring-0 transition duration-200 ease-in-out mt-0.5']" />
          </Switch>
        </SwitchGroup>

        <div v-if="isRoomHost" class="font-medium text-text-neutral-low text-[14px] leading-[20px] mb-3">User Preferences</div>

        <div class="mb-4 max-[800px]:mb-[18px]">
          <label class="block text-text-neutral-high text-[14px] leading-[20px] font-medium mb-2" :for="`#profile_name_${salt}`">{{ $t('session_setup_display_name') !== 'session_setup_display_name' ? $t('session_setup_display_name') : 'Display name' }}</label>
          <input type="text" required :name="`name_${salt}`" v-model="profile.name" class="border-2 border-[#292F36] rounded-[10px] bg-transparent font-normal text-text-neutral-extreme font-normal text-[16px] leading-[1.18rem] h-[44px] w-full px-[10px] outline-none focus:border-[#6CBFFF]" placeholder="" :id="`profile_name_${salt}`">
        </div>
        <!-- Voice tone selection -->
        <div class ="mb-3">
          <div class="font-medium text-text-neutral-extreme text-[16px] leading-[24px]">{{ $t('session_setup_voice_tone') !== 'session_setup_voice_tone' ? $t('session_setup_voice_tone') : 'Your voice tone' }}</div>
          <div class="text-[14px] leading-[20px] text-text-neutral-medium">{{ $t('session_setup_voice_tone_desc') !== 'session_setup_voice_tone_desc' ? $t('session_setup_voice_tone_desc') : 'Select the tone you want particpants to hear when captions are read out loud' }}</div>
        </div>
        
        <div class="flex bg-[#282F35] rounded-xl h-[50px] mb-5">
          <label class="justify-center cursor-pointer w-1/2 flex items-center m-0 font-normal pl-0" :id="`voice_male_${salt}`">
                <input type="radio" v-model="profile.voice" :name="`voice__${salt}`" value="male" class="hidden peer"/>
                <span class="peer-checked:bg-[#374D60] flex justify-center items-center h-full w-full rounded-xl"
                      :class="profile.voice === 'male' ? 'text-[#6CBFFF]' : 'text-neutral-400'">
                  <svg class="mr-[10px] align-middle" width="18" height="18" viewBox="0 0 15 14" xmlns="http://www.w3.org/2000/svg">
                    <path :fill="profile.voice==='male' ? '#6CBFFF' : '#C2C2C2'" d="M10.5 4.278C10.5 3.51572 10.4246 2.92207 10.2226 2.5332C10.1297 2.35444 10.0164 2.23292 9.87691 2.15104C9.73516 2.06786 9.52235 2 9.19201 2C9.0262 1.99993 8.86722 1.93817 8.7454 1.82812L8.70243 1.78516C8.70398 1.78683 8.70496 1.78832 8.70568 1.78906C8.70522 1.78861 8.70454 1.78788 8.70373 1.78711C8.698 1.78165 8.68366 1.76892 8.66076 1.75065C8.61474 1.71394 8.53315 1.65552 8.41271 1.5944C8.17506 1.47385 7.77044 1.33333 7.16141 1.33333C6.28311 1.33335 5.64308 1.59463 5.21935 2.03646C4.79252 2.48153 4.49995 3.19885 4.49995 4.278C4.49997 5.39597 4.68963 6.16207 4.98237 6.73893C5.27454 7.31463 5.69549 7.75354 6.24409 8.18294C6.40554 8.3093 6.49995 8.50332 6.49995 8.70833V10C6.49995 10.2942 6.3071 10.5539 6.02534 10.6387L3.55008 11.3822L3.55073 11.3828C2.91091 11.5787 2.4239 12.0648 2.21415 12.6667H12.7858C12.576 12.0648 12.089 11.5787 11.4492 11.3828L8.97456 10.6387C8.6928 10.5539 8.49995 10.2942 8.49995 10V8.70833C8.49995 8.50332 8.59437 8.3093 8.75581 8.18294C9.30441 7.75354 9.72536 7.31463 10.0175 6.73893C10.3103 6.16207 10.4999 5.39597 10.5 4.278ZM11.8333 4.278C11.8333 5.54854 11.6167 6.53306 11.2063 7.3418C10.8511 8.04175 10.3695 8.57066 9.83329 9.02409V9.50456L11.8359 10.1068L11.8391 10.1081C12.9896 10.4602 13.8462 11.3839 14.1308 12.5104C14.3571 13.4076 13.5687 13.9999 12.8958 14H2.10412C1.43131 13.9999 0.643236 13.4078 0.869744 12.5104C1.15429 11.3839 2.01028 10.4602 3.16076 10.1081L3.16402 10.1068L5.16662 9.50456V9.02409C4.6304 8.57066 4.14878 8.04176 3.79357 7.3418C3.38322 6.53306 3.16663 5.54854 3.16662 4.278C3.16662 2.96391 3.52611 1.8755 4.25711 1.11328C4.99119 0.347966 6.01483 1.54301e-05 7.16141 0C7.98616 0 8.59694 0.192844 9.01623 0.405599C9.20022 0.498962 9.34429 0.595106 9.45178 0.677083C9.86232 0.707788 10.2312 0.812947 10.552 1.0013C10.9414 1.2299 11.2164 1.55455 11.4056 1.91862C11.7675 2.61539 11.8333 3.49403 11.8333 4.278Z"/>
                  </svg>
                  {{ $t('enum_voicegender_male') }}
                </span>
          </label>
          <label class="justify-center cursor-pointer w-1/2 flex items-center m-0 font-normal pl-0" :id="`voice_female_${salt}`">
                <input type="radio" v-model="profile.voice" :name="`voice__${salt}`" value="female" class="hidden peer"/>
                <span class="peer-checked:bg-[#374D60] flex justify-center items-center h-full w-full rounded-xl"
                      :class="profile.voice === 'female' ? 'text-[#6CBFFF]' : 'text-neutral-400'">
                  <svg class="mr-[10px] align-middle" width="18" height="18" viewBox="0 0 15 14" xmlns="http://www.w3.org/2000/svg">
                    <path :fill="profile.voice==='female' ? '#6CBFFF' : '#C2C2C2'" d="M3.5 4.07488C3.50003 2.98743 3.75409 2.11223 4.20247 1.44728C4.65246 0.780055 5.27113 0.365308 5.92448 0.15952C6.57102 -0.0440616 7.24992 -0.0433244 7.84635 0.108088C8.30344 0.224126 8.74526 0.438223 9.09049 0.751317C9.5281 0.72135 9.93061 0.776728 10.2839 0.950535C10.7474 1.17863 11.0314 1.56165 11.2012 1.97918C11.365 2.38231 11.4337 2.84307 11.4668 3.29363C11.5002 3.74804 11.5 4.24541 11.5 4.73113C11.5 6.19625 11.9179 7.36157 12.0944 7.709C12.1805 7.87856 12.1898 8.07685 12.1204 8.25392C12.051 8.431 11.9095 8.57013 11.7311 8.63608C11.1534 8.84964 10.5144 9.01776 9.83333 9.13608V9.50392L11.8359 10.1068L11.8392 10.1074C12.9897 10.4595 13.8462 11.3833 14.1309 12.5098C14.357 13.4069 13.5687 14 12.8958 14H2.10417C1.4313 14 0.643121 13.4072 0.869791 12.5098C1.15442 11.3834 2.01033 10.4595 3.16081 10.1074L3.16406 10.1068L5.16667 9.50392V9.13608C4.48557 9.01776 3.8466 8.84964 3.26888 8.63608C3.09048 8.57013 2.94896 8.431 2.87956 8.25392C2.81021 8.07685 2.81952 7.87856 2.9056 7.709C3.17811 7.17276 3.5 6.29954 3.5 4.07488ZM4.83333 4.07488C4.83333 5.80483 4.64823 6.85533 4.39518 7.59832C4.86356 7.72907 5.37418 7.83288 5.91732 7.90171C6.25031 7.94397 6.49991 8.22751 6.5 8.56316V10C6.5 10.2942 6.30715 10.5539 6.02539 10.6387L3.55013 11.3822L3.55078 11.3828C2.91096 11.5788 2.42394 12.0648 2.21419 12.6667H12.7858C12.576 12.0648 12.089 11.5788 11.4492 11.3828L8.97461 10.6387C8.69285 10.5539 8.5 10.2942 8.5 10V8.56316C8.50009 8.22751 8.74969 7.94397 9.08268 7.90171C9.63112 7.83221 10.1464 7.72698 10.6185 7.59442C10.3915 6.90488 10.1667 5.89235 10.1667 4.73113C10.1667 4.23308 10.1665 3.78748 10.1374 3.39129C10.108 2.9912 10.0516 2.69296 9.96549 2.48113C9.88525 2.28385 9.7942 2.19516 9.69531 2.1465C9.58223 2.09088 9.3615 2.03992 8.94141 2.10874C8.69827 2.14859 8.45282 2.05135 8.30338 1.85548C8.16743 1.67715 7.89796 1.4968 7.51823 1.40041C7.14603 1.30593 6.72054 1.3067 6.32552 1.431C5.93744 1.5532 5.57661 1.79438 5.30794 2.19272C5.03762 2.59361 4.83337 3.19475 4.83333 4.07488Z"/>
                  </svg>
                  {{ $t('enum_voicegender_female') }}
                </span>
          </label>
        </div>

        <div class="mb-3">
          <div class="font-medium text-text-neutral-extreme text-[16px] leading-[24px]">{{ $t('captions_playback_mode') !== 'captions_playback_mode' ? $t('captions_playback_mode') : 'Caption playback mode' }}</div>
          <div class="text-[14px] leading-[20px] text-text-neutral-medium">{{ $t('captions_playback_mode_desc') !== 'captions_playback_mode_desc' ? $t('captions_playback_mode_desc') : 'Choose how buffered captions reveal: character-by-character or word-by-word.' }}</div>
        </div>

        <div class="flex bg-[#282F35] rounded-xl h-[50px] mb-5">
          <label class="justify-center cursor-pointer w-1/2 flex items-center m-0 font-normal pl-0" :id="`playback_character_${salt}`">
            <input type="radio" v-model="settings.partial_playback_mode" :name="`playback_mode_${salt}`" value="character" class="hidden peer"/>
            <span class="peer-checked:bg-[#374D60] flex justify-center items-center h-full w-full rounded-xl"
                  :class="settings.partial_playback_mode === 'character' ? 'text-[#6CBFFF]' : 'text-neutral-400'">
              {{ $t('captions_playback_mode_character') !== 'captions_playback_mode_character' ? $t('captions_playback_mode_character') : 'Character' }}
            </span>
          </label>
          <label class="justify-center cursor-pointer w-1/2 flex items-center m-0 font-normal pl-0" :id="`playback_word_${salt}`">
            <input type="radio" v-model="settings.partial_playback_mode" :name="`playback_mode_${salt}`" value="word" class="hidden peer"/>
            <span class="peer-checked:bg-[#374D60] flex justify-center items-center h-full w-full rounded-xl"
                  :class="settings.partial_playback_mode === 'word' ? 'text-[#6CBFFF]' : 'text-neutral-400'">
              {{ $t('captions_playback_mode_word') !== 'captions_playback_mode_word' ? $t('captions_playback_mode_word') : 'Word' }}
            </span>
          </label>
        </div>

        <div v-if="isAdminUser">
        <div class="mb-5">
          <div class="mb-2">
            <label class="block font-medium text-text-neutral-extreme text-[16px] leading-[24px]" :for="`asr_segmenter_mode_${salt}`">{{ $t('captions_segmenter_mode') !== 'captions_segmenter_mode' ? $t('captions_segmenter_mode') : 'Caption segmentation' }}</label>
            <div class="text-[14px] leading-[20px] text-text-neutral-medium">{{ $t('captions_segmenter_mode_desc') !== 'captions_segmenter_mode_desc' ? $t('captions_segmenter_mode_desc') : 'How live speech is split into captions. Shorter segments translate sooner, longer ones keep phrases intact.' }}</div>
          </div>
          <select
            v-model="settings.asr_segmenter_mode"
            :id="`asr_segmenter_mode_${salt}`"
            class="border-2 border-[#292F36] rounded-[10px] bg-[#171D24] text-text-neutral-extreme font-normal text-[16px] leading-[1.18rem] h-[44px] w-full px-[10px] outline-none focus:border-[#6CBFFF]"
          >
            <option value="stability">{{ $t('captions_segmenter_mode_stability') !== 'captions_segmenter_mode_stability' ? $t('captions_segmenter_mode_stability') : 'Stability' }}</option>
            <option value="phrase">{{ $t('captions_segmenter_mode_phrase') !== 'captions_segmenter_mode_phrase' ? $t('captions_segmenter_mode_phrase') : 'Phrase-aware' }}</option>
            <option value="sentence">{{ $t('captions_segmenter_mode_sentence') !== 'captions_segmenter_mode_sentence' ? $t('captions_segmenter_mode_sentence') : 'Sentence only' }}</option>
            <option value="punctuation">{{ $t('captions_segmenter_mode_punctuation') !== 'captions_segmenter_mode_punctuation' ? $t('captions_segmenter_mode_punctuation') : 'Any punctuation (default)' }}</option>
          </select>
        </div>

        <div class="mb-5" v-if="settings.asr_segmenter_mode === 'stability'">
          <div class="flex items-center justify-between gap-3 mb-2">
            <label class="block text-text-neutral-high text-[14px] leading-[20px] font-medium" :for="`asr_segment_max_duration_${salt}`">
              {{ $t('captions_segment_max_duration') !== 'captions_segment_max_duration' ? $t('captions_segment_max_duration') : 'Max segment duration' }}
            </label>
            <span class="text-[14px] leading-[20px] text-text-neutral-medium whitespace-nowrap">{{ asrSegmentMaxDurationDisplay }}</span>
          </div>
          <input
            type="range"
            min="4"
            max="30"
            step="1"
            v-model.number="settings.asr_segment_max_duration_sec"
            :id="`asr_segment_max_duration_${salt}`"
            class="w-full accent-[#6CBFFF]"
          >
          <div class="mt-1 flex justify-between text-[12px] leading-[16px] text-text-neutral-low">
            <span>4 s</span>
            <span>30 s</span>
          </div>
        </div>

          <div class="mb-5">
            <div class="flex items-center justify-between gap-3 mb-2">
              <label class="block text-text-neutral-high text-[14px] leading-[20px] font-medium" :for="`transcript_scroll_speed_${salt}`">
                {{ $t('captions_teleprompter_speed') !== 'captions_teleprompter_speed' ? $t('captions_teleprompter_speed') : 'Teleprompter speed' }}
              </label>
              <span class="text-[14px] leading-[20px] text-text-neutral-medium whitespace-nowrap">{{ transcriptScrollSpeedDisplay }}</span>
            </div>
            <input
              type="range"
              min="40"
              max="180"
              step="4"
              v-model.number="settings.transcript_scroll_speed"
              :id="`transcript_scroll_speed_${salt}`"
              class="w-full accent-[#6CBFFF]"
            >
            <div class="mt-1 flex justify-between text-[12px] leading-[16px] text-text-neutral-low">
              <span>{{ $t('common_slow') !== 'common_slow' ? $t('common_slow') : 'Slow' }}</span>
              <span>{{ $t('common_fast') !== 'common_fast' ? $t('common_fast') : 'Fast' }}</span>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3 mb-5">
            <div>
              <label class="block text-text-neutral-high text-[14px] leading-[20px] font-medium mb-2" :for="`asr_stability_${salt}`">{{ $t('captions_asr_stability') !== 'captions_asr_stability' ? $t('captions_asr_stability') : 'ASR stability' }}</label>
              <select
                v-model="settings.asr_partial_results_stability"
                :id="`asr_stability_${salt}`"
                class="border-2 border-[#292F36] rounded-[10px] bg-[#171D24] text-text-neutral-extreme font-normal text-[16px] leading-[1.18rem] h-[44px] w-full px-[10px] outline-none focus:border-[#6CBFFF]"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            <div>
              <label class="block text-text-neutral-high text-[14px] leading-[20px] font-medium mb-2" :for="`asr_segment_min_words_${salt}`">{{ $t('captions_asr_min_words') !== 'captions_asr_min_words' ? $t('captions_asr_min_words') : 'Min segment words' }}</label>
              <input
                type="number"
                min="1"
                max="30"
                step="1"
                v-model.number="settings.asr_segment_min_words"
                :id="`asr_segment_min_words_${salt}`"
                class="border-2 border-[#292F36] rounded-[10px] bg-transparent text-text-neutral-extreme font-normal text-[16px] leading-[1.18rem] h-[44px] w-full px-[10px] outline-none focus:border-[#6CBFFF]"
              >
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
// @ is an alias to /src
import { mapState } from 'vuex';
import { Switch, SwitchDescription, SwitchGroup, SwitchLabel } from '@headlessui/vue'

export default {
  name: 'SessionSettings',
  data() {
    return {
      settings: this.defaultSettings,
      profile: this.participant,
      sessionTitle: this.title,
      isRoomHost: this.isHost

  }
  },
  props: {
    defaultSettings: {
      type: Object,
      required: true
    },
    participant: {
      type: Object,
      required: true
    },
    title: {
      type: String,
      required: false,
      default: ''
    },
    isHost: {
      type: Boolean,
      required: true,
    },
    showHeader: {
      type: Boolean,
      required: false,
      default: true
    },
    salt: {
      type: String,
      required: false,
      default: 'popup'
    },

    minFontSize: Number,
    maxFontSize: Number,
  },
  components: {
    Switch,
    SwitchGroup,
    SwitchDescription,
    SwitchLabel
  },
  methods: {
    selfClose() {
      this.$emit('closed');
    },
    emitChanges(shouldUpdateProfile = false) {
      if (shouldUpdateProfile && this.authorized) {
        this.$store.dispatch("auth/update", this.profile);
      }
      
      this.$emit('saved', {
        settings: this.settings,
        profile: this.profile,
        title: this.sessionTitle
      });
    }
  },
  watch: {
    'settings.is_autostop_enabled'() {
      this.emitChanges(false);
    },
    'settings.profanity_filter'() {
      this.emitChanges(false);
    },
    'settings.partial_playback_mode'() {
      this.emitChanges(false);
    },
    'settings.asr_partial_results_stability'() {
      this.emitChanges(false);
    },
    'settings.asr_segment_min_words'() {
      this.emitChanges(false);
    },
    'settings.asr_segmenter_mode'() {
      this.emitChanges(false);
    },
    'settings.asr_segment_max_duration_sec'() {
      this.emitChanges(false);
    },
    'settings.partial_buffer_word_threshold'() {
      this.emitChanges(false);
    },
    'settings.partial_type_interval_ms'() {
      this.emitChanges(false);
    },
    'settings.transcript_scroll_speed'() {
      this.emitChanges(false);
    },
    'profile.name'() {
      this.emitChanges(true);
    },
    'profile.voice'() {
      this.emitChanges(true);
    },
    'sessionTitle'() {
      this.emitChanges(false);
    }
  },
  computed: {
     ...mapState('event', ['event', 'guest']),
     ...mapState('nonpersist', ['user']),
     ...mapState('lang', ['languages']),
     ...mapState('auth', ['authorized']),
      isAdminUser() {
       return !!(this.user && this.user.role === 'admin');
      },
      asrSegmentMaxDurationDisplay() {
        const value = Number(this.settings && this.settings.asr_segment_max_duration_sec);
        const safeValue = Number.isFinite(value) ? Math.round(value) : 10;
        return `${safeValue} s`;
      },
      transcriptScrollSpeedDisplay() {
        const value = Number(this.settings && this.settings.transcript_scroll_speed);
        const safeValue = Number.isFinite(value) ? Math.round(value) : 72;
        return `${safeValue} px/s`;
      },
  },
  mounted() {
    if (!this.settings.partial_playback_mode) {
      this.settings.partial_playback_mode = 'character';
    }
    if (!['low', 'medium', 'high'].includes(this.settings.asr_partial_results_stability)) {
      this.settings.asr_partial_results_stability = 'medium';
    }
    if (!this.settings.asr_segment_min_words || this.settings.asr_segment_min_words < 1) {
      this.settings.asr_segment_min_words = 5;
    }
    if (!['stability', 'phrase', 'sentence', 'punctuation'].includes(this.settings.asr_segmenter_mode)) {
      this.settings.asr_segmenter_mode = 'punctuation';
    }
    if (!this.settings.asr_segment_max_duration_sec || this.settings.asr_segment_max_duration_sec < 1) {
      this.settings.asr_segment_max_duration_sec = 10;
    }
    if (!this.settings.partial_buffer_word_threshold || this.settings.partial_buffer_word_threshold < 1) {
      this.settings.partial_buffer_word_threshold = 3;
    }
    if (!this.settings.partial_type_interval_ms || this.settings.partial_type_interval_ms < 5) {
      this.settings.partial_type_interval_ms = 22;
    }
    if (!this.settings.final_transcript_fade_ms || this.settings.final_transcript_fade_ms < 100) {
      this.settings.final_transcript_fade_ms = 2500;
    }
    if (!this.settings.transcript_scroll_speed || this.settings.transcript_scroll_speed < 40) {
      this.settings.transcript_scroll_speed = 72;
    }
  }
}
</script>
